/**
 * La búsqueda de convocatorias, con un solo texto.
 *
 * Antes eran tres desplegables —línea, perfil, área— más un botón de buscar
 * y otro de limpiar, ocupando un recuadro alto encima de la tabla. Hacían
 * falta tres gestos para algo que se resuelve escribiendo.
 *
 * Ahora un texto busca a la vez en el nombre de la convocatoria, en el de su
 * línea y en sus áreas o expresiones, que es lo que la gente tiene en la
 * cabeza cuando busca.
 */

/** Sin tildes, sin mayúsculas y sin espacios de más. */
const normalizar = (valor: any): string =>
  String(valor ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/** El nombre de la línea de una convocatoria, si está en el maestro. */
const nombreDeLaLinea = (convocatoria: any, lineas: any[]): string => {
  const lista = Array.isArray(lineas) ? lineas : [];
  const linea = lista.find((l: any) => String(l?.id) === String(convocatoria?.linea_convocatoria));
  return String(linea?.nombre ?? '');
};

/** Las áreas o expresiones, en un solo texto. */
const textoDeLasAreas = (convocatoria: any): string =>
  (Array.isArray(convocatoria?.area) ? convocatoria.area : [])
    .map((a: any) => String(a?.text ?? a?.nombre ?? ''))
    .join(' ');

/**
 * ¿Esta convocatoria responde a lo que se escribió?
 *
 * Todas las palabras tienen que aparecer en alguna parte, no necesariamente
 * en la misma: así "danza 2026" encuentra la de danza de ese año aunque el
 * área y el nombre vayan por separado.
 */
export const coincideConLaBusqueda = (
  convocatoria: any,
  texto: string,
  lineas: any[] = []
): boolean => {
  const buscado = normalizar(texto);
  if (buscado === '') return true;

  const dondeBuscar = normalizar(
    [
      convocatoria?.nombre_convocatoria,
      nombreDeLaLinea(convocatoria, lineas),
      textoDeLasAreas(convocatoria),
      convocatoria?.entidad,
    ].join(' ')
  );

  return buscado.split(' ').every((palabra) => dondeBuscar.indexOf(palabra) > -1);
};

/** Las que responden, en el mismo orden en que venían. */
export const filtrarPorBusqueda = (
  convocatorias: any,
  texto: string,
  lineas: any[] = []
): any[] =>
  (Array.isArray(convocatorias) ? convocatorias : []).filter((c: any) =>
    coincideConLaBusqueda(c, texto, lineas)
  );
